import { friendlyDbError } from '../../../utils/friendlyDbError';
/**
 * Config slice — carica la configurazione WAHA da api_configurations e inizializza il service.
 * Estratto da whatsappChatStore.ts (loadConfig).
 */

import type { StateCreator } from 'zustand';
import type { ConfigSlice, WhatsAppChatStore, WahaConfig } from '../types';
import { supabase } from '../../../supabase';
import { initWahaService } from '../../../services/wahaApiService';
import { getProvider } from '../../../services/whatsapp/providerFactory';

export const createConfigSlice: StateCreator<
  WhatsAppChatStore,
  [],
  [],
  ConfigSlice
> = (set) => ({
  // ============ INITIAL STATE ============
  wahaConfig: null,
  provider: null,
  configLoading: false,
  configError: null,

  // ============ ACTIONS ============
  loadConfig: async (organizationId: string, userId?: string, configId?: string) => {
    set({ configLoading: true, configError: null });

    try {
      let data = null;
      let error = null;

      // Se configId fornito, fetch diretto senza fallback
      if (configId) {
        const result = await supabase
          .from('api_configurations')
          .select('*')
          .eq('id', configId)
          .eq('is_active', true)
          .single();

        data = result.data;
        error = result.error;
      }

      // Prima prova con userId se fornito (e no configId)
      if (!configId && userId) {
        const result = await supabase
          .from('api_configurations')
          .select('*')
          .eq('service_name', 'waha')
          .eq('organization_id', organizationId)
          .eq('is_active', true)
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        data = result.data;
        error = result.error;
      }

      // Fallback: cerca config globale (senza user_id), rispettando restrizioni accesso
      if (!data && !configId) {
        const result = await supabase
          .from('api_configurations')
          .select('*')
          .eq('service_name', 'waha')
          .eq('organization_id', organizationId)
          .eq('is_active', true)
          .is('user_id', null)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (result.data && userId) {
          // Controlla se questa config ha restrizioni in whatsapp_user_access
          const { data: accessRows } = await supabase
            .from('whatsapp_user_access')
            .select('user_id')
            .eq('configuration_id', result.data.id)
            .eq('organization_id', organizationId);

          if (accessRows && accessRows.length > 0) {
            // Config con restrizioni: carica solo se l'utente ha accesso
            const hasAccess = accessRows.some(r => r.user_id === userId);
            if (hasAccess) {
              data = result.data;
            }
            // Se non ha accesso, data resta null → "nessuna config"
          } else {
            // Nessuna restrizione: accessibile a tutti
            data = result.data;
          }
        } else {
          data = result.data;
          error = result.error;
        }
      }

      if (error) throw error;
      if (!data) throw new Error('Configurazione WAHA non trovata');

      const isWaha = data.service_name === 'waha';

      const config: WahaConfig = {
        apiUrl: data.configuration?.api_url || '',
        apiKey: data.api_key || '',
        session: data.configuration?.session || '',
        // Cloud: il numero E.164 è in configuration.display_phone_number (popolato dal cron);
        // WAHA: in configuration.phone_number.
        phoneNumber: data.configuration?.phone_number || data.configuration?.display_phone_number || '',
        organizationId,
        configId: data.id,
        serviceName: data.service_name,
      };

      // Inizializza il service WAHA solo per i provider WAHA (CloudProvider è self-contained).
      if (isWaha) {
        initWahaService(config);
      }

      // Istanzia il provider corretto via factory. Per Cloud servono phone_number_id + waba_id
      // (colonne su api_configurations).
      let provider = null;
      try {
        provider = isWaha
          ? getProvider({ serviceName: 'waha' })
          : getProvider({
              serviceName: data.service_name,
              configId: data.id,
              organizationId,
              phoneNumberId: data.phone_number_id,
              wabaId: data.waba_id,
            });
      } catch (provErr) {
        console.error('[Store] getProvider failed:', provErr);
      }

      // Ripristina mapping @lid -> @c.us da localStorage
      let restoredLidMap = new Map<string, string>();
      try {
        const savedMap = localStorage.getItem('whatsapp_lid_map');
        if (savedMap) {
          const entries = JSON.parse(savedMap) as [string, string][];
          restoredLidMap = new Map(entries);
          console.log('[Store] Restored @lid mapping from localStorage:', restoredLidMap.size, 'entries');
        }
      } catch {
        // Ignore parse errors
      }

      set({ wahaConfig: config, provider, configLoading: false, lidToCusMap: restoredLidMap });
    } catch (error) {
      console.error('[Store] loadConfig error:', error);
      set({
        configError: friendlyDbError(error) || 'Errore caricamento config',
        configLoading: false,
      });
    }
  },
});
